import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { fmtCVE, fmtDate, QUOTE_STATUS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Buildings, CircleNotch, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BackButton } from "@/components/BackButton";

export default function PublicQuote() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/public/quotes/${token}`);
      setQuote(data);
    } catch { setError(true); }
  };

  useEffect(() => { load(); }, [token]);

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/public/quotes/${token}/decision`, { decision, comment });
      toast.success(decision === "approved" ? "Orçamento aprovado. Obrigado!" : "Orçamento rejeitado.");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (error)
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 text-muted-foreground" data-testid="public-quote-not-found">
        Orçamento não encontrado.
        <a href="/" className="rounded-full border border-border px-4 py-2 text-sm font-semibold text-foreground" data-testid="public-quote-back">← Voltar</a>
      </div>
    );
  if (!quote)
    return <div className="flex h-screen items-center justify-center"><CircleNotch size={28} className="animate-spin text-primary" /></div>;

  const st = QUOTE_STATUS[quote.status] || QUOTE_STATUS.rascunho;
  const decided = ["aprovado", "rejeitado"].includes(quote.status);

  return (
    <div className="min-h-screen bg-secondary/40 py-8 px-4">
      <div className="mx-auto max-w-2xl space-y-4">
        <BackButton fallback="/" className="-ml-3" testid="public-quote-back" />
        <div className="rounded-xl border border-border bg-card p-6 md:p-8" data-testid="public-quote">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                <Buildings size={22} weight="fill" />
              </div>
              <div>
                <div className="font-display text-lg font-extrabold tracking-tight">{quote.organization?.name}</div>
                <div className="text-xs text-muted-foreground">NIF: {quote.organization?.nif || "—"}</div>
              </div>
            </div>
            <Badge variant="outline" className={st.cls}>{st.label}</Badge>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Orçamento</p>
              <h1 className="font-display text-2xl font-extrabold">{quote.number}</h1>
            </div>
            <div className="text-right text-sm">
              <div className="text-muted-foreground">Cliente</div>
              <div className="font-medium">{quote.client?.name}</div>
              <div className="mt-1 text-muted-foreground">Válido até {fmtDate(quote.valid_until)}</div>
            </div>
          </div>

          <div className="mt-6 space-y-2">
            {quote.items.map((it, i) => {
              const net = Number(it.quantity) * Number(it.unit_price) * (1 - Number(it.discount || 0) / 100);
              return (
                <div key={i} className="flex items-center justify-between border-b border-border/60 pb-2 text-sm last:border-0">
                  <div>
                    <div className="font-medium">{it.description}</div>
                    <div className="text-xs text-muted-foreground">{Number(it.quantity).toLocaleString("pt-PT")}{it.unit ? ` ${it.unit}` : ""} × {fmtCVE(it.unit_price)} · IVA {it.vat_rate}%</div>
                  </div>
                  <span className="font-medium">{fmtCVE(net)}</span>
                </div>
              );
            })}
          </div>

          <div className="ml-auto mt-4 max-w-[240px] space-y-1 text-sm">
            <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{fmtCVE(quote.totals?.subtotal)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>IVA</span><span>{fmtCVE(quote.totals?.vat_total)}</span></div>
            <div className="flex justify-between border-t border-border pt-1 text-base font-bold text-primary"><span>Total</span><span>{fmtCVE(quote.totals?.total)}</span></div>
          </div>

          {quote.payment_terms && <p className="mt-4 text-sm text-muted-foreground"><b>Condições:</b> {quote.payment_terms}</p>}
        </div>

        {decided ? (
          <div className="rounded-xl border border-border bg-card p-6 text-center" data-testid="public-decided">
            <p className="text-sm">
              Este orçamento foi <b>{st.label.toLowerCase()}</b>.
              {quote.decision_comment && <span className="block mt-1 text-muted-foreground">"{quote.decision_comment}"</span>}
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card p-6" data-testid="public-decision">
            <h3 className="mb-3 font-display font-bold">Responder ao orçamento</h3>
            <Textarea placeholder="Comentário (opcional)" value={comment} onChange={(e) => setComment(e.target.value)}
              className="mb-3" data-testid="public-comment" />
            <div className="flex gap-3">
              <Button className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700" disabled={busy}
                onClick={() => decide("approved")} data-testid="approve-btn">
                <CheckCircle size={18} className="mr-1.5" /> Aprovar
              </Button>
              <Button variant="outline" className="flex-1 rounded-full border-red-200 text-red-700 hover:bg-red-50" disabled={busy}
                onClick={() => decide("rejected")} data-testid="reject-btn">
                <XCircle size={18} className="mr-1.5" /> Rejeitar
              </Button>
            </div>
          </div>
        )}
        <p className="text-center text-xs text-muted-foreground">Powered by GESTA CV</p>
      </div>
    </div>
  );
}
